import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ClearFormDataActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import {
  CreatePayment,
  PaymentDeclined,
  PaymentSuccess,
} from '@app/finxone-web-frontend/app/lib/actions/payment.action';
import {
  TransferMoneyRequest,
  paymentRailOptions,
} from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { ConfirmationService } from '@app/finxone-web-frontend/app/lib/services/confirmation-service/confirmation-service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import {
  ActionButtonOutcomesType,
  ActionButtonWidgetActionsOutcomesNavigationConfigMap,
  BaseWidgetProperties,
  ButtonActionType,
  ConfirmationClaimType,
  RoleNavigationConfigType,
  actionButtonNavigationConfigType,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { take, tap } from 'rxjs';
import { createRedirectionUrlFromNavigationOptions } from '../../../utils';
import { formatSchedulePaymentDate } from './payment-utils';

export function submitUnifiedPayment(widgetProperties: BaseWidgetProperties, injector: Injector) {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const confirmationService = injector.get(ConfirmationService);
  const isSmsAuthEnabled = store.selectSnapshot(
    ProjectSettingsState.getProjectSettings,
  )?.smsAuthenticationEnabled;
  const actionButtonNavigationConfig: actionButtonNavigationConfigType =
    widgetProperties?.['actionButtonNavigationConfig'] ??
    ActionButtonWidgetActionsOutcomesNavigationConfigMap.get(ButtonActionType.SUBMIT_PAYMENT);

  const targetUrl = createRedirectionUrlFromNavigationOptions(
    actionButtonNavigationConfig?.[ActionButtonOutcomesType.PROCESSING] as RoleNavigationConfigType,
  );
  if (isSmsAuthEnabled) {
    const subscription = confirmationService.checkConfirmation(targetUrl).subscribe(() => {
      createPayloadAndCallPaymentsApi(injector, actionButtonNavigationConfig);
      subscription.unsubscribe();
    });
    confirmationService.confirmAction(ConfirmationClaimType.MAKE_PAYMENT, targetUrl);
  } else {
    createPayloadAndCallPaymentsApi(injector, actionButtonNavigationConfig);
    router.navigate([targetUrl]);
  }
}

/**
 * Submits a unified payment by creating a payload from form and account data,
 * and dispatching a payment action. If SMS authentication is enabled, it waits
 * for confirmation before proceeding. Navigates to a target URL based on the
 * action button navigation configuration.
 *
 * @param widgetProperties - Properties of the widget, including navigation config.
 * @param injector - Angular's Injector to retrieve services and states.
 */
function createPayloadAndCallPaymentsApi(
  injector: Injector,
  actionButtonNavigationConfig: actionButtonNavigationConfigType,
) {
  const store = injector.get(Store);
  const actions = injector.get(Actions);
  const schedulePaymentDate = formatSchedulePaymentDate(injector);

  const formStateData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.BENEFICIARY_PAYMENT_CURRENCY_SELECTION),
  )?.formData;
  const activeAccount = store.selectSnapshot(AccountState.getCurrentAccount);
  const sepaFormState = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT),
  )?.formData;

  if (!activeAccount) {
    Sentry.addBreadcrumb({
      category: 'Submit Unified Payment',
      message: 'No active account found on submit',
      level: 'error',
    });
    console.error('No active account found');
    throw new Error('No active account found');
  }

  if (!formStateData) {
    Sentry.addBreadcrumb({
      category: 'Submit Unified Payment',
      message: 'Form state data missing on submit',
      level: 'error',
    });
    console.error('Payment details not found');
    throw new Error('Payment details not found');
  }
  // start creating the payload with formStateData
  // and activeAccount
  let payload: TransferMoneyRequest = {
    sourceAccountId: activeAccount?.accountId ?? '',
    bid: formStateData?.selectedBeneficiaryId,
    amount: formStateData?.amount,
    paymentReference: formStateData?.paymentReference,
    transferReasonId: formStateData?.transferReasonId,
    schedulePaymentDate,
  };

  // Add scheme only if sepaFormState and paymentRail are available
  if (sepaFormState?.paymentRail) {
    payload = {
      ...payload,
      ...sepaFormState,
      scheme: paymentRailOptions[sepaFormState.paymentRail as SEPAPaymentRailTypeEnum],
    };
  }

  // Check for intl payments, if not gbp then add review to the payload
  if (activeAccount?.currency !== 'GBP' && activeAccount?.currency !== 'EUR') {
    payload = {
      ...payload,
      review: false,
    };
  }

  if (formStateData) {
    setupResponseListeners(actions, injector, actionButtonNavigationConfig);
    store.dispatch(new CreatePayment(payload));
  }
}

/**
 * Sets up listeners for payment response actions and handles navigation based on the outcome.
 *
 * @param actions - The NGXS Actions stream to listen for dispatched actions.
 * @param injector - The Angular Injector to retrieve services like Router and Store.
 * @param actionButtonNavigationConfig - Configuration mapping action outcomes to navigation targets.
 *
 * Listens for PaymentSuccess and PaymentDeclined actions, clears form data related to SEPA payments,
 * determines the outcome type, and navigates to the appropriate URL based on the action outcome.
 */
function setupResponseListeners(
  actions: Actions,
  injector: Injector,
  actionButtonNavigationConfig: actionButtonNavigationConfigType,
) {
  const router = injector.get(Router);
  const store = injector.get(Store);

  actions
    .pipe(
      ofActionDispatched(PaymentSuccess, PaymentDeclined),
      take(1),
      tap((event: PaymentSuccess | PaymentDeclined) => {
        store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT));
        store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT_RAIL_FLAG));
        const requestOutcome =
          event instanceof PaymentSuccess
            ? ActionButtonOutcomesType.SUCCESS
            : ActionButtonOutcomesType.FAILURE;
        const targetUrl = createRedirectionUrlFromNavigationOptions(
          actionButtonNavigationConfig?.[requestOutcome] as RoleNavigationConfigType,
        );
        if (event instanceof PaymentDeclined) {
          Sentry.addBreadcrumb({
            category: 'Submit Unified Payment',
            message: 'Payment submission failed',
            level: 'error',
          });
        }
        router.navigate([targetUrl]);
      }),
    )
    .subscribe();
}
